// @ts-check
import { MARKER_CONFIG } from "../config/constants.js";
import { Logger } from "../utils/Logger.js";
import { DOMUtils } from "../utils/DOMUtils.js";
import { CameraUtils } from "../utils/CameraUtils.js";
import { PositioningStrategyFactory } from "../strategies/PositioningStrategy.js";
import { MarkerCommentPanel } from "../components/MarkerCommentPanel.js";

/**
 * Service for managing marker lifecycle and operations
 */
export class MarkerManager {
	constructor(viewer, options = {}) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("MarkerManager");
		this.domUtils = new DOMUtils();
		this.cameraUtils = new CameraUtils(viewer);
		this.commentPanel = new MarkerCommentPanel(
			viewer,
			"markerCommentPanel",
			"Marker Comment",
			{ fileId: options.fileId, apiActions: options.mainExtension.apiActions }
		);

		// Store reference to main extension for markup drawing
		this.mainExtension = options.mainExtension || null;

		// Store user data for marker creation
		this.userImageLink = options.userImageLink || null;
		this.userName = options.userName || "";

		this.fileId = options.fileId || null;

		// Marker storage
		this.markers = new Map(); // id -> MarkerData
		this.dbIdToMarkerId = new Map(); // dbId -> markerId
		this.counter = 0;

		// Active marker tracking
		this.activeMarkerId = null;

		// Positioning strategy
		this.positioningStrategy =
			PositioningStrategyFactory.createStrategy(viewer);

		// Performance optimization
		this.positioningQueue = new Set();
		this.isProcessingQueue = false;
	}

	/**
	 * Create a new marker on an object
	 * @param {number} dbId - Database ID of the object
	 * @returns {Promise<Object|null>} Created marker data or null
	 */
	async createMarker(dbId) {
		// Check if marker already exists
		if (this.dbIdToMarkerId.has(dbId)) {
			const existingMarkerId = this.dbIdToMarkerId.get(dbId);
			this.logger.warn(
				`Marker already exists for dbId ${dbId} (marker ID: ${existingMarkerId})`
			);
			return this.markers.get(existingMarkerId);
		}

		// Check marker limit
		if (this.markers.size >= MARKER_CONFIG.MAX_MARKERS) {
			this.logger.error(
				`Maximum number of markers (${MARKER_CONFIG.MAX_MARKERS}) reached`
			);
			return null;
		}

		try {
			// Create a provisional marker first
			this.counter++;
			const markerId = this.counter;

			// Create DOM element without comment initially
			const element = this.domUtils.createMarkerElement(
				markerId,
				dbId,
				this.userImageLink,
				this.userName,
				"" // Empty comment initially
			);

			// Add click handler
			this.domUtils.addClickHandler(element, () => {
				this.onMarkerClick(markerId);
			});

			// Add to container
			this.domUtils.addToContainer(element, this.viewer.container);

			// Capture current camera position
			const cameraData = this.cameraUtils.captureCamera();
			if (cameraData) {
				this.logger.debug(`Captured camera position for marker ${markerId}`);
			} else {
				this.logger.warn(
					`Failed to capture camera position for marker ${markerId}`
				);
			}

			// Create provisional marker data
			const markerData = {
				id: markerId,
				dbId,
				element,
				timestamp: Date.now(),
				metadata: {},
				cameraData: cameraData,
				comment: "", // Empty initially
				userName: this.userName || "Anonymous",
				userImageLink: this.userImageLink || null,
				replies: [],
			};

			// Store marker provisionally
			this.markers.set(markerId, markerData);
			this.dbIdToMarkerId.set(dbId, markerId);

			// Calculate initial position
			await this.updateMarkerPosition(markerId);

			// Disable markup mode after successful creation (user finished adding initial comment)
			if (this.mainExtension && this.mainExtension.disableMarkupMode) {
				try {
					this.mainExtension.disableMarkupMode();
					this.logger.debug("Markup mode disabled after new marker creation");
				} catch (e) {
					this.logger.warn(
						"Failed to disable markup mode after marker creation",
						e
					);
				}
			}

			// Show comment panel for new marker and wait for user input
			const commentData = await this.showNewMarkerCommentPanel(markerData);

			// If user cancelled (closed panel), remove the marker
			if (!commentData) {
				this.removeMarker(markerId);
				return null;
			}

			// Update marker with comment data
			markerData.comment = commentData.text || "";
			if (commentData.attachedFiles && commentData.attachedFiles.length > 0) {
				markerData.metadata.files = commentData.attachedFiles;
			}

			// The DOM element doesn't need updating for comment text as it's handled by the panel

			if (this.mainExtension && this.mainExtension.enableMarkupDrawing) {
				this.mainExtension.enableMarkupDrawing();
			}

			return markerData;
		} catch (error) {
			this.logger.error(`Error creating marker for dbId ${dbId}`, error);
			return null;
		}
	}

	/**
	 * Remove a marker by ID
	 * @param {number} markerId - Marker ID to remove
	 * @returns {boolean} True if removed successfully
	 */
	removeMarker(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Marker ${markerId} not found for removal`);
			return false;
		}

		try {
			// Clear active state if this marker is active
			if (this.activeMarkerId === markerId) {
				this.activeMarkerId = null;
			}

			// Remove from DOM
			this.domUtils.removeElement(marker.element);

			// Remove from storage
			this.markers.delete(markerId);
			this.dbIdToMarkerId.delete(marker.dbId);

			// Remove from positioning queue if present
			this.positioningQueue.delete(markerId);

			return true;
		} catch (error) {
			this.logger.error(`Error removing marker ${markerId}`, error);
			return false;
		}
	}

	/**
	 * Clear all markers
	 */
	clearAllMarkers() {
		// Remove all DOM elements
		for (const marker of this.markers.values()) {
			this.domUtils.removeElement(marker.element);
		}

		// Clear storage
		this.markers.clear();
		this.dbIdToMarkerId.clear();
		this.positioningQueue.clear();
		this.counter = 0;

		// Clear active marker
		this.activeMarkerId = null;
	}

	/**
	 * Update marker position
	 * @param {number} markerId - Marker ID
	 * @returns {Promise<boolean>} True if updated successfully
	 */
	async updateMarkerPosition(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Marker ${markerId} not found for position update`);
			return false;
		}

		try {
			// Check if this is a 2D point marker (no dbId)
			// if (marker.dbId === null && marker.modelPoint) {
			if (marker.dbId === null && marker.cameraData.viewType === "2D") {
				// Try using positioning strategy first for 2D point markers
				if (
					this.positioningStrategy &&
					typeof this.positioningStrategy.updatePositionFor2DPoint ===
						"function"
				) {
					try {
						const result =
							await this.positioningStrategy.updatePositionFor2DPoint(marker);
						if (result && result.position) {
							this.domUtils.positionElement(marker.element, result.position, {
								isVisible: result.isVisible,
								isOnScreen: result.isOnScreen,
							});
							return true;
						}
					} catch (error) {
						this.logger.warn(
							"Positioning strategy failed for 2D point marker, using fallback:",
							error
						);
					}
				}

				// Fallback to direct 2D point positioning
				return await this.updateMarkerPositionAt2DPoint(markerId);
			}

			// Use regular object-based positioning strategy
			const result = await this.positioningStrategy.updatePosition(marker);

			if (result && result.position) {
				// Apply position to element
				this.domUtils.positionElement(marker.element, result.position, {
					isVisible: result.isVisible,
					isOnScreen: result.isOnScreen,
				});

				return true;
			} else {
				this.logger.warn(`Failed to calculate position for marker ${markerId}`);
				return false;
			}
		} catch (error) {
			this.logger.error(
				`Error updating position for marker ${markerId}`,
				error
			);
			return false;
		}
	}

	/**
	 * Update all marker positions (with queue processing)
	 * @returns {Promise<void>}
	 */
	async updateAllMarkerPositions() {
		if (this.markers.size === 0) return;

		// Add all markers to positioning queue
		for (const markerId of this.markers.keys()) {
			this.positioningQueue.add(markerId);
		}

		// Process queue
		await this.processPositioningQueue();
	}

	/**
	 * Process positioning queue efficiently
	 * @returns {Promise<void>}
	 */
	async processPositioningQueue() {
		if (this.isProcessingQueue || this.positioningQueue.size === 0) {
			return;
		}

		this.isProcessingQueue = true;

		try {
			const markerIds = Array.from(this.positioningQueue);
			this.positioningQueue.clear();

			// Process in batches to avoid blocking UI
			const batchSize = 10;
			for (let i = 0; i < markerIds.length; i += batchSize) {
				const batch = markerIds.slice(i, i + batchSize);

				// Process batch
				await Promise.all(
					batch.map((markerId) => this.updateMarkerPosition(markerId))
				);

				// Yield control to allow UI updates
				if (i + batchSize < markerIds.length) {
					await new Promise((resolve) => setTimeout(resolve, 1));
				}
			}
		} finally {
			this.isProcessingQueue = false;
		}
	}

	/**
	 * Handle marker click
	 * @param {number} markerId - Clicked marker ID
	 */
	async onMarkerClick(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Clicked marker ${markerId} not found`);
			return;
		}

		try {
			// Toggle active state
			this.setActiveMarker(markerId);

			// Highlight the associated object
			this.viewer.select([marker.dbId]);

			// Camera transition duration (ms)
			const cameraTransitionDuration = 200;

			// Restore camera position if available
			if (marker.cameraData) {
				const restored = await this.cameraUtils.restoreCamera(
					marker.cameraData,
					{
						animate: true,
						duration: cameraTransitionDuration,
					}
				);

				if (!restored) {
					this.logger.warn(
						`Failed to restore camera position for marker ${markerId}, falling back to fit to view`
					);
					this.viewer.fitToView([marker.dbId]);
				}
			} else {
				// Fallback to original fit to view behavior
				this.viewer.fitToView([marker.dbId]);
			}

			// Wait for camera transition to complete, then show comment panel
			setTimeout(() => {
				this.showCommentPanel(marker);
			}, cameraTransitionDuration);
		} catch (error) {
			this.logger.error(`Error handling click for marker ${markerId}`, error);
		}
	}

	/**
	 * Show comment panel for a marker
	 * @param {Object} marker - Marker data
	 */
	showCommentPanel(marker) {
		// Enable markup drawing when marker is active
		if (this.mainExtension && this.mainExtension.enableMarkupDrawing) {
			this.mainExtension.enableMarkupDrawing();
		}

		this.commentPanel.showForMarker(
			marker,
			(replyData) => {
				// Handle reply submission - replyData can be either string (legacy) or object with text and attachedFiles
				const isLegacyFormat = typeof replyData === "string";
				const replyText = isLegacyFormat ? replyData : replyData.text;
				const attachedFiles = isLegacyFormat
					? []
					: replyData.attachedFiles || [];

				// Create reply object
				const reply = {
					user: this.userName || "Unknown User",
					text: replyText,
					timestamp: Date.now(),
					attachedFiles: attachedFiles,
				};

				// Add reply with API integration
				this.commentPanel.addReply(reply, {
					fileId: this.fileId, // Building discipline file ID
					onSuccess: (apiReply) => {
						marker.replies.push(apiReply);
					},
					onError: (error) => {
						this.logger.error(
							`Failed to add reply to marker ${marker.id}:`,
							error
						);
					},
				});
			},
			() => {
				// Handle panel close
				this.deactivateMarker(marker.id);
			}
		);
	}

	/**
	 * Show comment panel for new marker creation
	 * @param {Object} marker - Marker data
	 * @returns {Promise<Object|null>} Comment data or null if cancelled
	 */
	showNewMarkerCommentPanel(marker) {
		return new Promise((resolve) => {
			// Setup temporary callbacks for new marker creation
			const originalOnReplyCallback = this.commentPanel.onReplyCallback;
			const originalOnCloseCallback = this.commentPanel.onCloseCallback;

			// Show panel for new marker
			this.commentPanel.createNewMarker(marker);

			// Override callbacks for new marker creation
			this.commentPanel.onReplyCallback = (replyData) => {
				// Restore original callbacks
				this.commentPanel.onReplyCallback = originalOnReplyCallback;
				this.commentPanel.onCloseCallback = originalOnCloseCallback;

				// Close the panel
				this.commentPanel.close();

				// Resolve with the comment data
				resolve(replyData);
			};

			this.commentPanel.onCloseCallback = () => {
				// Restore original callbacks
				this.commentPanel.onReplyCallback = originalOnReplyCallback;
				this.commentPanel.onCloseCallback = originalOnCloseCallback;

				// Resolve with null (cancelled)
				resolve(null);
			};
		});
	}

	/**
	 * Set a marker as active (green state) and deactivate others
	 * @param {number} markerId - Marker ID to set as active
	 * @returns {boolean} True if successful
	 */
	setActiveMarker(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Cannot set marker ${markerId} as active - not found`);
			return false;
		}

		try {
			// Deactivate current active marker if different
			if (this.activeMarkerId && this.activeMarkerId !== markerId) {
				this.deactivateMarker(this.activeMarkerId);
			}

			// Check if this marker is already active
			if (this.activeMarkerId === markerId) {
				// If clicking the same marker, deactivate it
				this.deactivateMarker(markerId);
				return true;
			}

			// Set new active marker
			this.activeMarkerId = markerId;
			this.domUtils.setMarkerActive(marker.element);

			return true;
		} catch (error) {
			this.logger.error(`Error setting marker ${markerId} as active`, error);
			return false;
		}
	}

	/**
	 * Deactivate a specific marker
	 * @param {number} markerId - Marker ID to deactivate
	 * @returns {boolean} True if successful
	 */
	deactivateMarker(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Cannot deactivate marker ${markerId} - not found`);
			return false;
		}

		try {
			this.domUtils.setMarkerInactive(marker.element);

			if (this.activeMarkerId === markerId) {
				this.activeMarkerId = null;

				// Close comment panel if it's open
				if (this.commentPanel && this.commentPanel.isVisible()) {
					this.commentPanel.close();
				}

				// Disable markup drawing when no marker is active
				if (this.mainExtension && this.mainExtension.disableMarkupDrawing) {
					this.mainExtension.disableMarkupDrawing();
				}
			}

			this.logger.debug(`Marker ${markerId} deactivated`);
			return true;
		} catch (error) {
			this.logger.error(`Error deactivating marker ${markerId}`, error);
			return false;
		}
	}

	/**
	 * Deactivate all markers
	 */
	deactivateAllMarkers() {
		if (this.activeMarkerId) {
			this.deactivateMarker(this.activeMarkerId);
		}

		// Ensure comment panel is closed
		if (this.commentPanel && this.commentPanel.isVisible()) {
			this.commentPanel.close();
		}

		// Disable markup drawing
		if (this.mainExtension && this.mainExtension.disableMarkupDrawing) {
			this.mainExtension.disableMarkupDrawing();
		}

		this.logger.debug("All markers deactivated");
	}

	/**
	 * Get currently active marker ID
	 * @returns {number|null} Active marker ID or null
	 */
	getActiveMarkerId() {
		return this.activeMarkerId;
	}

	/**
	 * Check if a marker is active
	 * @param {number} markerId - Marker ID to check
	 * @returns {boolean} True if marker is active
	 */
	isMarkerActive(markerId) {
		return this.activeMarkerId === markerId;
	}

	/**
	 * Highlight a marker (for hover effects from the marker list)
	 * @param {number} markerId - Marker ID to highlight
	 * @param {boolean} isHighlighted - Whether to highlight or unhighlight
	 * @returns {boolean} True if successful
	 */
	highlightMarker(markerId, isHighlighted) {
		const marker = this.markers.get(markerId);
		if (!marker) {
			this.logger.warn(`Cannot highlight marker ${markerId} - not found`);
			return false;
		}

		try {
			if (isHighlighted) {
				// Add hover highlight effect to the marker element
				this.domUtils.setMarkerHovered(marker.element);

				// Highlight the associated object in the viewer if it has a dbId
				if (marker.dbId && marker.dbId !== -1) {
					// Use temporary highlighting (different from selection)
					try {
						if (this.viewer.impl && this.viewer.impl.visibilityManager) {
							this.viewer.impl.visibilityManager.setNodeHighlighted(
								marker.dbId,
								true,
								false
							);
						} else {
							// Fallback: use selection highlight if visibilityManager is not available
							this.viewer.select([marker.dbId]);
						}
					} catch (error) {
						this.logger.warn(
							`Failed to highlight object ${marker.dbId}:`,
							error
						);
					}
				}
			} else {
				// Remove hover highlight effect
				this.domUtils.setMarkerUnhovered(marker.element);

				// Remove highlight from the associated object
				if (marker.dbId && marker.dbId !== -1) {
					try {
						if (this.viewer.impl && this.viewer.impl.visibilityManager) {
							this.viewer.impl.visibilityManager.setNodeHighlighted(
								marker.dbId,
								false,
								false
							);
						} else {
							// Fallback: clear selection if using selection as highlight
							this.viewer.clearSelection();
						}
					} catch (error) {
						this.logger.warn(
							`Failed to unhighlight object ${marker.dbId}:`,
							error
						);
					}
				}
			}

			this.logger.debug(
				`Marker ${markerId} ${isHighlighted ? "highlighted" : "unhighlighted"}`
			);
			return true;
		} catch (error) {
			this.logger.error(
				`Error ${isHighlighted ? "highlighting" : "unhighlighting"} marker ${markerId}`,
				error
			);
			return false;
		}
	}

	/**
	 * Update positioning strategy (e.g., when view type changes)
	 * @param {string} viewType - '2D' or '3D'
	 */
	updatePositioningStrategy(viewType) {
		const oldStrategy = this.positioningStrategy.getStrategyName();
		this.positioningStrategy = PositioningStrategyFactory.createStrategy(
			this.viewer
		);
		const newStrategy = this.positioningStrategy.getStrategyName();

		if (oldStrategy !== newStrategy) {
			// Clear all markers when strategy changes to avoid positioning issues
			if (this.markers.size > 0) {
				this.clearAllMarkers();
			}
		}
	}

	/**
	 * Get marker by ID
	 * @param {number} markerId - Marker ID
	 * @returns {Object|null} Marker data or null
	 */
	getMarker(markerId) {
		return this.markers.get(markerId) || null;
	}

	/**
	 * Get marker by dbId
	 * @param {number} dbId - Database ID
	 * @returns {Object|null} Marker data or null
	 */
	getMarkerByDbId(dbId) {
		const markerId = this.dbIdToMarkerId.get(dbId);
		return markerId ? this.markers.get(markerId) : null;
	}

	/**
	 * Get all markers as array
	 * @returns {Array} Array of marker data
	 */
	getAllMarkers() {
		return Array.from(this.markers.values());
	}

	/**
	 * Get marker count
	 * @returns {number} Number of markers
	 */
	getMarkerCount() {
		return this.markers.size;
	}

	/**
	 * Export markers data for persistence
	 * @returns {Object} Export data
	 */
	exportData() {
		const markers = this.getAllMarkers().map((marker) => ({
			id: marker.id,
			dbId: marker.dbId,
			metadata: marker.metadata,
			cameraData: marker.cameraData,
			comment: marker.comment || "",
			userName: marker.userName || "Anonymous",
			replies: marker.replies || [],
			// Include 2D marker-specific data
			modelPoint: marker.modelPoint || null,
			model: marker.model ? { is2d: marker.model.is2d() } : null,
		}));

		return {
			version: 1,
			counter: this.counter,
			markers,
			timestamp: Date.now(),
		};
	}

	/**
	 * Import markers data from saved state
	 * @param {Object} data - Import data
	 * @returns {Promise<boolean>} True if imported successfully
	 */
	async importData(data) {
		if (!data || !Array.isArray(data.markers)) {
			this.logger.error("Invalid import data format");
			return false;
		}

		try {
			// Clear existing markers
			this.clearAllMarkers();

			// Restore counter
			this.counter = data.counter || 0;

			// Recreate markers with preserved camera data
			for (const markerData of data.markers) {
				const marker = await this.createMarkerWithData(markerData);
				if (!marker) {
					this.logger.warn(
						`Failed to import marker for dbId ${markerData.dbId}`
					);
				}
			}

			return true;
		} catch (error) {
			this.logger.error("Error importing markers data", error);
			return false;
		}
	}

	/**
	 * Create a marker with preserved data (used during import)
	 * @param {Object} importedMarkerData - Imported marker data
	 * @returns {Promise<Object|null>} Created marker data or null
	 */
	async createMarkerWithData(importedMarkerData) {
		const {
			dbId,
			metadata,
			cameraData,
			id,
			comment,
			userName,
			userImageLink,
			replies,
			modelPoint,
			model,
		} = importedMarkerData;

		// Check if marker already exists (for 3D markers with dbId)
		if (dbId && this.dbIdToMarkerId.has(dbId)) {
			const existingMarkerId = this.dbIdToMarkerId.get(dbId);
			this.logger.warn(
				`Marker already exists for dbId ${dbId} (marker ID: ${existingMarkerId})`
			);
			return this.markers.get(existingMarkerId);
		}

		// Check marker limit
		if (this.markers.size >= MARKER_CONFIG.MAX_MARKERS) {
			this.logger.error(
				`Maximum number of markers (${MARKER_CONFIG.MAX_MARKERS}) reached`
			);
			return null;
		}

		try {
			// Use the imported ID or increment counter
			const markerId = id;

			// Create DOM element with the original user data (not current session user)
			const element = this.domUtils.createMarkerElement(
				markerId,
				dbId || -1, // Use -1 for 2D markers without dbId
				userImageLink || null, // Use the imported user's image link
				userName || "Unknown", // Use the imported user's name
				comment || ""
			);

			// Add click handler
			this.domUtils.addClickHandler(element, () => {
				this.onMarkerClick(markerId);
			});

			// Add to container
			this.domUtils.addToContainer(element, this.viewer.container);

			// Create marker data with preserved camera data and comment
			const markerData = {
				id: markerId,
				dbId: dbId || null,
				element,
				timestamp: Date.now(),
				metadata: metadata || {},
				cameraData: cameraData || null,
				comment: comment || "",
				userName: userName || "Unknown",
				userImageLink: userImageLink || null, // Store the original user's image link
				replies: replies || [],
				// Restore 2D marker data if present
				modelPoint: modelPoint || null,
				model: model ? this.viewer.model : null,
			};

			// Store marker
			this.markers.set(markerId, markerData);
			if (dbId) {
				this.dbIdToMarkerId.set(dbId, markerId);
			}

			// Calculate initial position
			await this.updateMarkerPosition(markerId);

			return markerData;
		} catch (error) {
			this.logger.error(`Error creating marker with preserved data`, error);
			return null;
		}
	}

	/**
	 * Get manager statistics
	 * @returns {Object} Statistics
	 */
	getStats() {
		return {
			markerCount: this.markers.size,
			counter: this.counter,
			queueSize: this.positioningQueue.size,
			isProcessing: this.isProcessingQueue,
			strategy: this.positioningStrategy.getStrategyName(),
			activeMarkerId: this.activeMarkerId,
		};
	}

	/**
	 * Update user data for new markers
	 * @param {string} userImageLink - New user image URL
	 * @param {string} userName - New user name
	 */
	updateUserData(userImageLink, userName) {
		this.userImageLink = userImageLink;
		this.userName = userName;
		this.logger.debug(
			`User data updated in MarkerManager: ${userName || "Anonymous"}`
		);
	}

	/**
	 * Create a new marker at an arbitrary 2D model point (no dbId required)
	 * @param {object} model - The 2D model reference
	 * @param {object} modelPoint - The 2D model coordinates (Vector3)
	 * @returns {Promise<Object|null>} Created marker data or null
	 */
	async createMarkerAt2DPoint(model, modelPoint) {
		// Check marker limit
		if (this.markers.size >= MARKER_CONFIG.MAX_MARKERS) {
			this.logger.error(
				`Maximum number of markers (${MARKER_CONFIG.MAX_MARKERS}) reached`
			);
			return null;
		}

		try {
			// Create a provisional marker first
			this.counter++;
			const markerId = this.counter;

			// Create DOM element without comment initially (no dbId)
			const element = this.domUtils.createMarkerElement(
				markerId,
				-1, // Use -1 to indicate no dbId for 2D point markers
				this.userImageLink,
				this.userName,
				"" // Empty comment initially
			);

			// Add click handler
			this.domUtils.addClickHandler(element, () => {
				this.onMarkerClick(markerId);
			});

			// Add to container
			this.domUtils.addToContainer(element, this.viewer.container);

			// Capture current camera position
			const cameraData = this.cameraUtils.captureCamera();

			// Create provisional marker data
			const markerData = {
				id: markerId,
				dbId: null,
				model,
				modelPoint,
				element,
				timestamp: Date.now(),
				metadata: {},
				cameraData: cameraData,
				comment: "", // Empty initially
				userName: this.userName || "Anonymous",
				replies: [],
			};

			// Store marker provisionally
			this.markers.set(markerId, markerData);

			// Position the marker immediately using the unified positioning method
			await this.updateMarkerPosition(markerId);

			// Disable markup mode after successful creation (user finished adding initial comment)
			if (this.mainExtension && this.mainExtension.disableMarkupMode) {
				try {
					this.mainExtension.disableMarkupMode();
					this.logger.debug(
						"Markup mode disabled after new 2D point marker creation"
					);
				} catch (e) {
					this.logger.warn(
						"Failed to disable markup mode after 2D point marker creation",
						e
					);
				}
			}

			// Show comment panel for new marker and wait for user input
			const commentData = await this.showNewMarkerCommentPanel(markerData);

			// If user cancelled (closed panel), remove the marker
			if (!commentData) {
				this.removeMarker(markerId);
				return null;
			}

			// Update marker with comment data
			markerData.comment = commentData.text || "";
			if (commentData.attachedFiles && commentData.attachedFiles.length > 0) {
				markerData.metadata.files = commentData.attachedFiles;
			}

			return markerData;
		} catch (error) {
			this.logger.error("Error creating marker at 2D point", error);
			return null;
		}
	}

	/**
	 * Update marker position for 2D point markers
	 * @param {number} markerId
	 * @returns {Promise<boolean>}
	 */
	async updateMarkerPositionAt2DPoint(markerId) {
		const marker = this.markers.get(markerId);
		if (!marker || !marker.modelPoint) return false;

		try {
			let clientPos = null;

			// Method 1: Try using the viewer's worldToClient method
			if (this.viewer && typeof this.viewer.worldToClient === "function") {
				try {
					clientPos = this.viewer.worldToClient(marker.modelPoint);
					this.logger.debug(
						"World to client conversion successful:",
						clientPos
					);
				} catch (error) {
					this.logger.warn("World to client conversion failed:", error);
				}
			}

			// Method 2: Last resort - center of viewport
			if (!clientPos) {
				const rect = this.viewer.container.getBoundingClientRect();
				clientPos = {
					x: rect.width / 2,
					y: rect.height / 2,
				};
				this.logger.warn(
					"Using viewport center as fallback position for marker",
					markerId
				);
			}

			// Position the marker DOM element
			marker.element.style.left = `${clientPos.x}px`;
			marker.element.style.top = `${clientPos.y}px`;
			marker.element.style.position = "absolute";
			marker.element.style.zIndex = "1000";
			marker.element.style.visibility = "visible";

			this.logger.debug(
				`Positioned 2D point marker ${markerId} at (${clientPos.x}, ${clientPos.y})`
			);
			return true;
		} catch (error) {
			this.logger.error("Error updating marker position at 2D point", error);
			return false;
		}
	}
}
